/* eslint-disable hfr-tokens/no-hardcoded-design-tokens -- intentional brand hex colors and large icon px for public rev-share signing page */
// src/features/sign/RevShareSigningPage.jsx
// Public-facing page for HFR approvers to countersign a Referral Agreement.
// Reached via the emailed link:
//   /admin/settings/agreements/sign/rev-share?id=<agreement_id>&token=<token>
// No authentication is required — the one-time token is the auth mechanism.
// @viewport both — managers/admins often open the email link on phone/tablet; TOUCH + fluid widths.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SecurityIcon from '@mui/icons-material/Security';
import { TOUCH, FONT_SIZE } from '@/core/platform/platformStandards';
import agreementService from '@/services/agreementService';
import AgreementPdfViewer from '@/components/agreements/AgreementPdfViewer';

const HFR_NAME = 'HFR Asset Management, LLC';

// ─── PageShell ─────────────────────────────────────────────────────────────────
// IMPORTANT: defined OUTSIDE RevShareSigningPage so React never remounts it on
// re-renders triggered by state changes (e.g. typing in a TextField).

const PageShell = ({ children }) => (
  <Box
    sx={{
      minHeight: '100dvh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}
  >
    <Box
      component="header"
      sx={{
        bgcolor: '#1a3a5c',
        py: { xs: 1.25, sm: 1.5 },
        px: { xs: 2, md: 4 },
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: TOUCH.comfySize,
        flexShrink: 0,
      }}
    >
      <GavelIcon sx={{ color: '#fff' }} />
      <Typography
        variant="subtitle1"
        sx={{
          color: '#fff',
          fontWeight: 700,
          fontSize: { xs: FONT_SIZE.md, sm: FONT_SIZE.lg },
          lineHeight: 1.3,
        }}
      >
        HFRConnect — Agreement Signing
      </Typography>
    </Box>

    <Container
      maxWidth="md"
      sx={{
        py: { xs: 2, sm: 3, md: 5 },
        px: { xs: 2, sm: 3 },
        flex: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      {children}
    </Container>

    <Box component="footer" sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">
        © {new Date().getFullYear()} HFR Asset Management, LLC · HFRConnect Platform
      </Typography>
    </Box>
  </Box>
);

// ─── RevShareSigningPage ───────────────────────────────────────────────────────

const RevShareSigningPage = () => {
  const theme = useTheme();

  const params = new URLSearchParams(window.location.search);
  const agreementId = params.get('id');
  const token = params.get('token');

  const [loadState, setLoadState] = useState('loading'); // loading | ready | error | done
  const [pageError, setPageError] = useState('');
  const [agreementData, setAgreementData] = useState(null);

  const [name, setName] = useState('');
  const [sigTitle, setSigTitle] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const loadData = useCallback(async () => {
    if (!agreementId || !token) {
      setPageError('This link is incomplete. Please use the link from your email.');
      setLoadState('error');
      return;
    }
    try {
      setLoadState('loading');
      const data = await agreementService.getHfrSigningData(agreementId, token);
      setAgreementData(data);
      setLoadState('ready');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Unable to load signing page.';
      setPageError(msg);
      setLoadState('error');
    }
  }, [agreementId, token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    setFormError('');
    if (!name.trim()) { setFormError('Please enter your full name.'); return; }
    if (!confirmed) { setFormError('Please confirm your intent to sign electronically.'); return; }

    setIsSubmitting(true);
    try {
      await agreementService.submitHfrSignature({
        agreement_id: agreementId,
        token,
        name: name.trim(),
        title: sigTitle.trim(),
      });

      setLoadState('done');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Signing failed. Please try again.';
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <PageShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 6, sm: 8 } }}>
          <CircularProgress size={TOUCH.iconSize} aria-label="Loading signing page" />
        </Box>
      </PageShell>
    );
  }

  if (loadState === 'error') {
    return (
      <PageShell>
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, textAlign: 'center', maxWidth: '100%' }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Unable to Load Signing Page</Typography>
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{pageError}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            If you received this link in an email, please contact{' '}
            <strong>the HFRConnect admin</strong> to request a new signing link.
          </Typography>
        </Paper>
      </PageShell>
    );
  }

  if (loadState === 'done') {
    return (
      <PageShell>
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, textAlign: 'center', maxWidth: '100%' }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Agreement Countersigned
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have successfully countersigned the Referral Agreement with{' '}
            <strong>{agreementData?.firm_name}</strong> on behalf of {HFR_NAME}.
          </Typography>
          <Alert severity="success">
            The agreement is now fully executed. The manager and HFRConnect admin have been notified.
          </Alert>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            You may close this window.
          </Typography>
        </Paper>
      </PageShell>
    );
  }

  // ── Main signing form ──────────────────────────────────────────────────────
  const advisorSig = agreementData?.advisor_signature || {};

  return (
    <PageShell>
      {/* Info banner */}
      <Alert
        severity="info"
        icon={<SecurityIcon />}
        sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, maxWidth: '100%' }}
      >
        You are about to electronically countersign a legally binding agreement on behalf of{' '}
        <strong>{HFR_NAME}</strong>. Your name, title, and signing metadata will be permanently recorded.
      </Alert>

      {/* Advisor firm info */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2, maxWidth: '100%' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Advisor Firm</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {agreementData?.firm_name || '[Advisor Firm]'}
        </Typography>
        {agreementData?.firm_address && (
          <Typography variant="body2" color="text.secondary">{agreementData.firm_address}</Typography>
        )}

        {advisorSig?.signature_name && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.08), borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">Advisor signed by</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{advisorSig.signature_name}</Typography>
            {advisorSig.signature_title && (
              <Typography variant="caption" color="text.secondary">{advisorSig.signature_title}</Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Agreement text */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2, maxWidth: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Referral Agreement
        </Typography>
        <AgreementPdfViewer
          pdfUrl={agreementData?.agreement_pdf_url}
          isLoading={false}
          error={agreementData?.agreement_pdf_url ? '' : 'The agreement PDF could not be retrieved. You may still proceed to countersign below.'}
          title="Referral Agreement"
          minHeight={{ xs: 'min(58vh, 400px)', sm: 380 }}
          showOpenInNewTab
        />
      </Paper>

      {/* HFR signature form */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2, maxWidth: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Your Signature ({HFR_NAME})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          By entering your name and clicking &ldquo;Sign Agreement&rdquo; you are signing this document
          electronically with the same legal effect as a handwritten signature.
        </Typography>

        <TextField
          label="Full Name *"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="As it should appear on the agreement"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              minHeight: TOUCH.minSize,
              fontSize: FONT_SIZE.sm,
            },
          }}
          inputProps={{ maxLength: 120, enterKeyHint: 'next' }}
          disabled={isSubmitting}
        />
        <TextField
          label="Title / Role"
          fullWidth
          value={sigTitle}
          onChange={(e) => setSigTitle(e.target.value)}
          placeholder="e.g. Managing Director"
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              minHeight: TOUCH.minSize,
              fontSize: FONT_SIZE.sm,
            },
          }}
          inputProps={{ maxLength: 120, enterKeyHint: 'done' }}
          disabled={isSubmitting}
        />

        <Divider sx={{ mb: 2 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={isSubmitting}
              sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize, p: 0.5, alignSelf: 'flex-start' }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: { xs: FONT_SIZE.sm, sm: FONT_SIZE.md } }}>
              I confirm that I am authorised to sign this agreement on behalf of{' '}
              <strong>{HFR_NAME}</strong> and that my electronic signature constitutes
              a legally binding commitment.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mb: 2, gap: 1, ml: 0, '& .MuiFormControlLabel-label': { minWidth: 0 } }}
        />

        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim() || !confirmed}
            fullWidth
            startIcon={
              isSubmitting
                ? <CircularProgress size={TOUCH.iconSize} color="inherit" aria-hidden />
                : <GavelIcon />
            }
            sx={{
              minHeight: TOUCH.comfySize,
              maxWidth: { sm: 400 },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 0, sm: 280 },
              fontSize: FONT_SIZE.sm,
              py: 1.25,
            }}
          >
            {isSubmitting ? 'Recording Signature…' : 'Sign Agreement'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        Signing link expires:{' '}
        {agreementData?.expires_at
          ? new Date(agreementData.expires_at).toLocaleDateString('en-US', { dateStyle: 'long' })
          : 'N/A'}
      </Typography>
    </PageShell>
  );
};

export default RevShareSigningPage;
